import { toStringOrUndefined } from 'helpers'
import { CreateDocumentRequestV3, CreateDocumentRequestV3TypeEnum } from '../../../../api/models/create-document-request-v3'
import { UpdateDocumentRequestV3, UpdateDocumentRequestV3TypeEnum } from '../../../../api/models/update-document-request-v3'
import {
  CreateDocumentRequestWithFiles,
  DocumentCreate, DocumentUpdate,
  isPreviousDocumentCreate, isPreviousDocumentUpdate,
  isSupportingDocumentCreate, isSupportingDocumentUpdate,
  isTransportDocumentCreate, isTransportDocumentUpdate,
  PreviousDocumentCreate, PreviousDocumentUpdate,
  SupportingDocumentCreate, SupportingDocumentUpdate,
  TransportDocumentCreate, TransportDocumentUpdate, UpdateDocumentRequestWithFiles,
} from '../../document/models/request'

export function toCreateConsignmentPreviousDocumentRequest(
  document: PreviousDocumentCreate,
  transitOperationId: number,
  consignmentId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.PREVIOUS,
      referenceNumber: document.referenceNumber,
      complementOfInformation: document.complementOfInformation,
      transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
      documentType: document.documentType,
      transitOperationId,
      consignmentId,
      procedureType: document.procedureType ?? undefined,
    },
    files: document.files,
  }
}

export function toUpdateConsignmentPreviousDocumentRequest(
  document: PreviousDocumentUpdate,
  transitOperationId: number,
  consignmentId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.PREVIOUS,
      referenceNumber: document.referenceNumber,
      complementOfInformation: document.complementOfInformation,
      transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
      documentType: document.documentType,
      transitOperationId,
      consignmentId,
      procedureType: document.procedureType ?? undefined,
    },
    files: document.files,
  }
}

export function toCreateConsignmentTransportDocumentRequest(
  document: TransportDocumentCreate,
  transitOperationId: number,
  consignmentId: number,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.TRANSPORT,
      transitOperationId,
      consignmentId,
    },
    files: document.files,
  }
}

export function toUpdateConsignmentTransportDocumentRequest(
  document: TransportDocumentUpdate,
  transitOperationId: number,
  consignmentId: number,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.TRANSPORT,
      transitOperationId,
      consignmentId,
    },
    files: document.files,
  }
}

export function toCreateConsignmentSupportingDocumentRequest(
  document: SupportingDocumentCreate,
  transitOperationId: number,
  consignmentId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      complementOfInformation: document.complementOfInformation,
      documentLineItemNumber: toStringOrUndefined(document.documentLineNumber),
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.SUPPORTING,
      transitOperationId,
      consignmentId,
    },
    files: document.files,
  }
}

export function toUpdateConsignmentSupportingDocumentRequest(
  document: SupportingDocumentUpdate,
  transitOperationId: number,
  consignmentId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      complementOfInformation: document.complementOfInformation,
      documentLineItemNumber: toStringOrUndefined(document.documentLineNumber),
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.SUPPORTING,
      transitOperationId,
      consignmentId,
    },
    files: document.files,
  }
}

export function toCreateDocumentRequests(
  documents: DocumentCreate[],
  transitOperationId: number,
  consignmentId: number,
): CreateDocumentRequestV3[] {
  const transportDocumentRequests = documents.filter(isTransportDocumentCreate)
    .map((d) => toCreateConsignmentTransportDocumentRequest(d, transitOperationId, consignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const previousDocumentRequests = documents.filter(isPreviousDocumentCreate)
    .map((d) => toCreateConsignmentPreviousDocumentRequest(d, transitOperationId, consignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const supportingDocumentRequests = documents.filter(isSupportingDocumentCreate)
    .map((d) => toCreateConsignmentSupportingDocumentRequest(d, transitOperationId, consignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  return [
    ...transportDocumentRequests,
    ...previousDocumentRequests,
    ...supportingDocumentRequests,
  ]
}

export function toUpdateDocumentRequests(
  documents: DocumentUpdate[],
  transitOperationId: number,
  consignmentId: number,
): UpdateDocumentRequestV3[] {
  const transportDocumentRequests = documents.filter(isTransportDocumentUpdate)
    .map((d) => toUpdateConsignmentTransportDocumentRequest(d, transitOperationId, consignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const previousDocumentRequests = documents.filter(isPreviousDocumentUpdate)
    .map((d) => toUpdateConsignmentPreviousDocumentRequest(d, transitOperationId, consignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const supportingDocumentRequests = documents.filter(isSupportingDocumentUpdate)
    .map((d) => toUpdateConsignmentSupportingDocumentRequest(d, transitOperationId, consignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  return [
    ...transportDocumentRequests,
    ...previousDocumentRequests,
    ...supportingDocumentRequests,
  ]
}
